/**
 * Announcements — landlord-side. Compose a message that fans out to
 * every tenant you've touched (invites + rent payers), with audience
 * preview and per-recipient delivery report.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Textarea, Select } from "@/components/ui/Primitives";
import { Megaphone, Users, CheckCircle2, AlertTriangle, Send, Eye, History, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const IMPORTANCE = [
  { value: "normal",    label: "Normal" },
  { value: "important", label: "Important" },
  { value: "urgent",    label: "Urgent" },
];
const TONES = [
  { value: "friendly", label: "Friendly" },
  { value: "formal",   label: "Formal" },
  { value: "urgent",   label: "Urgent" },
];

export default function Announcements() {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id || user?.email || "";
  const [audience, setAudience] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", body: "", importance: "normal", send_email: true });
  const [sending, setSending] = useState(false);
  const [detail, setDetail] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftForm, setDraftForm] = useState({ topic: "", tone: "friendly" });

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        fetch(`${API}/api/landlord/announcements/audience-preview`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid }}).then(r => r.json()),
        fetch(`${API}/api/landlord/announcements`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid }}).then(r => r.json()),
      ]);
      setAudience(a);
      setItems(h.items || []);
    } finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  async function send(testOnly = false) {
    if (!form.subject || !form.body) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      const r = await fetch(`${API}/api/landlord/announcements`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify({ ...form, test_only: testOnly }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not send");
      if (testOnly) {
        toast.success("Test delivery created — check your inbox.");
      } else {
        toast.success(`Sent to ${d.delivery_sent}/${d.delivery_total} tenants${d.delivery_failed ? ` · ${d.delivery_failed} failed` : ""}`);
        setForm({ subject: "", body: "", importance: "normal", send_email: true });
      }
      load();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setSending(false); }
  }

  async function openDetail(aid) {
    const r = await fetch(`${API}/api/landlord/announcements/${aid}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid }});
    const d = await r.json();
    if (r.ok) setDetail(d);
  }

  async function generateDraft() {
    if (!draftForm.topic.trim()) { toast.error("Describe what to announce first."); return; }
    setDrafting(true);
    try {
      const r = await fetch(`${API}/api/landlord/announcements/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify({ topic: draftForm.topic.trim(), tone: draftForm.tone, importance: form.importance }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not draft");
      if (d.smart_engine_available === false || (!d.subject && !d.body)) {
        toast.info(d.notice || "The drafting copilot is briefly unavailable — please try again in a moment.");
        setDraftOpen(false);
        return;
      }
      setForm((f) => ({ ...f, subject: d.subject || f.subject, body: d.body || f.body }));
      setDraftOpen(false);
      setDraftForm({ topic: "", tone: "friendly" });
      toast.success("Draft ready — edit before sending.");
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setDrafting(false); }
  }

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Announcements"
        subtitle="Broadcast a message to every tenant you've invited or collected rent from. Each tenant receives an email + sees it in their portal."
      />

      <Card className="p-5 mb-6" data-testid="compose-announcement">
        <div className="flex items-start gap-3 mb-4 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><Megaphone className="w-5 h-5"/></div>
          <div className="flex-1">
            <div className="font-display text-base font-extrabold text-slate-900">Send a new announcement</div>
            <div className="text-xs text-slate-600 mt-0.5">
              <Users className="w-3 h-3 inline -mt-0.5"/> Audience: <b data-testid="audience-count">{audience?.count || 0}</b> tenant{audience?.count === 1 ? "" : "s"}
              {(audience?.sample?.length || 0) > 0 && <span className="text-slate-500"> · {audience.sample.map(s => s.name).join(", ")}{audience.count > audience.sample.length ? ` + ${audience.count - audience.sample.length} more` : ""}</span>}
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 -mt-1">
            <Button type="button" variant="secondary" onClick={() => setDraftOpen(true)} className="!py-1.5 !px-3 text-xs" data-testid="announcement-copilot-btn">
              <Sparkles className="w-3.5 h-3.5"/> Write for me (copilot)
            </Button>
            <span className="text-[11px] text-slate-500">Describe anything you want to say — the copilot will draft a polished subject + body for you to edit.</span>
          </div>
          <Input label="Subject" required maxLength={160} value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} placeholder="Heads-up: building elevator maintenance Saturday" testid="announcement-subject"/>
          <Textarea label="Message" required rows={6} maxLength={10000} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} placeholder="Hi everyone — the elevator at 123 Bloor will be offline Sat 9am-2pm for inspection. Please plan your day accordingly. Thanks!" testid="announcement-body"/>
          <div className="flex flex-wrap items-center gap-3">
            <Select label="Importance" value={form.importance} onChange={(e) => setForm({...form, importance: e.target.value})}>
              {IMPORTANCE.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </Select>
            <label className="text-sm inline-flex items-center gap-2 mt-5">
              <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({...form, send_email: e.target.checked})} data-testid="send-email-toggle"/>
              <Mail className="w-3.5 h-3.5 text-slate-500"/> Also email tenants (recommended)
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
          <Button variant="ghost" onClick={() => send(true)} disabled={sending || !form.subject || !form.body} data-testid="announcement-test"><Eye className="w-4 h-4"/> Test (send to me only)</Button>
          <Button onClick={() => send(false)} disabled={sending || !form.subject || !form.body || !audience?.count} data-testid="announcement-send">{sending ? "Sending…" : <><Send className="w-4 h-4"/> Send to {audience?.count || 0} tenant{audience?.count === 1 ? "" : "s"}</>}</Button>
        </div>
        {!audience?.count && (
          <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5">
            <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5"/> No tenants on file yet. Invite a tenant first via Send Lease / Tenant Invites, or wait for a rent payment to come through.
          </div>
        )}
      </Card>

      <h3 className="font-display text-lg font-bold text-slate-900 mb-3 inline-flex items-center gap-2"><History className="w-4 h-4 text-slate-500"/> Sent history</h3>
      {items.length === 0 ? (
        <EmptyState icon={Megaphone} title="No announcements yet" description="Your sent announcements will appear here with a per-recipient delivery report."/>
      ) : (
        <div className="space-y-2">
          {items.map(a => (
            <Card key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer hover:border-emerald-300" onClick={() => openDetail(a.id)} data-testid={`announcement-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900 truncate">{a.subject}</span>
                  {a.importance === "urgent" && <Pill tone="red">Urgent</Pill>}
                  {a.importance === "important" && <Pill tone="amber">Important</Pill>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{new Date(a.created_at).toLocaleString("en-CA", {dateStyle: "medium", timeStyle: "short"})}</div>
              </div>
              <div className="shrink-0 flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><CheckCircle2 className="w-3.5 h-3.5"/> {a.delivery_sent}/{a.delivery_total}</span>
                {a.delivery_failed > 0 && <span className="inline-flex items-center gap-1 text-rose-600 font-bold"><AlertTriangle className="w-3.5 h-3.5"/> {a.delivery_failed} failed</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={draftOpen} onClose={() => setDraftOpen(false)} title="Write with the announcement copilot" testid="announcement-draft-modal">
        <div className="space-y-4">
          <Textarea
            label="Tell the copilot what to say"
            required
            rows={4}
            placeholder="E.g. Snow-clearing this Friday morning between 7–10am, avoid parking in the driveway."
            value={draftForm.topic}
            onChange={(e) => setDraftForm({ ...draftForm, topic: e.target.value })}
            testid="announcement-draft-topic"
          />
          <Select label="Tone" value={draftForm.tone} onChange={(e) => setDraftForm({ ...draftForm, tone: e.target.value })} testid="announcement-draft-tone">
            {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setDraftOpen(false)}>Cancel</Button>
            <Button onClick={generateDraft} disabled={drafting || !draftForm.topic.trim()} data-testid="announcement-draft-submit">
              <Sparkles className="w-4 h-4"/> {drafting ? "Writing…" : "Draft it"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.subject || ""} testid="announcement-detail-modal">
        {detail && (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Message</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{detail.body}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Recipients" value={detail.delivery_total} tone="slate"/>
              <Stat label="Email sent" value={detail.delivery_sent} tone="green"/>
              <Stat label="Failed" value={detail.delivery_failed} tone={detail.delivery_failed ? "rose" : "slate"}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Per-recipient delivery</div>
              <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="deliveries-list">
                {(detail.deliveries || []).map(d => (
                  <div key={d.id} className="flex items-center justify-between text-xs border-b border-slate-100 py-1.5" data-testid={`delivery-${d.id}`}>
                    <div className="min-w-0 truncate">
                      <span className="font-bold text-slate-900">{d.tenant_name || d.tenant_email}</span>
                      {d.tenant_email && <span className="text-slate-500"> · {d.tenant_email}</span>}
                    </div>
                    <Pill tone={d.email_status === "sent" ? "green" : d.email_status === "failed" ? "red" : "slate"}>{d.email_status}</Pill>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const TONE = { green: "bg-emerald-50 border-emerald-200 text-emerald-900", rose: "bg-rose-50 border-rose-200 text-rose-900", slate: "bg-slate-50 border-slate-200 text-slate-900" }[tone];
  return (
    <div className={`rounded-lg border p-3 text-center ${TONE}`}>
      <div className="font-display text-2xl font-extrabold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
    </div>
  );
}
